import React, { useState } from 'react';

interface UserFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (event: React.FormEvent) => void;
  title: string;
  initialFormData: any;
  companies: any[];
  departments: any[];
  loadingCompanies: boolean;
  loadingDepartments: boolean;
  formLoading: boolean;
  formError: string | null;
  formSuccess: string | null;
  isEditMode: boolean;
  onFormChange: (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
  userRole?: 'SUPER_ADMIN' | 'ADMIN' | 'COMPANY_OWNER' | 'USER'; // Add userRole prop to determine available options
  onAddChildCompany?: (parentCompany: any) => void; // Update callback to pass parent company data
}

const UserFormModal: React.FC<UserFormProps> = ({
  isOpen,
  onClose,
  onSubmit,
  title,
  initialFormData,
  companies,
  departments = [],
  loadingCompanies,
  loadingDepartments = false,
  formLoading,
  formError,
  formSuccess,
  isEditMode,
  onFormChange,
  userRole = 'ADMIN', // Default to ADMIN if not provided
  onAddChildCompany
}) => {
  const [showPassword, setShowPassword] = useState(false);
  
  if (!isOpen) return null;

  // Determine available user types based on the current user's role
  const getUserTypeOptions = () => {
    if (userRole === 'SUPER_ADMIN') {
      // SuperAdmin: Admin, Company Owner, User (Super Admin removed)
      return [
        { value: 'user', label: 'User' },
        { value: 'admin', label: 'Admin' },
        { value: 'company owner', label: 'Company Owner' }
      ];
    } else if (userRole === 'ADMIN') {
      // Admin: User only (Super Admin, Admin, and Company Owner removed)
      return [
        { value: 'user', label: 'User' }
      ];
    } else if (userRole === 'COMPANY_OWNER') {
      // Company Owner: Admin, User (Super Admin and Company Owner removed)
      return [
        { value: 'user', label: 'User' },
        { value: 'admin', label: 'Admin' }
      ];
    } else {
      // User: User, Company Owner (only these two)
      return [
        { value: 'user', label: 'User' },
        { value: 'company owner', label: 'Company Owner' }
      ];
    }
  };

  const userTypeOptions = getUserTypeOptions();

  // Email validation function
  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Handle form submission with validation
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate email if present in form data
    if (initialFormData.email && !validateEmail(initialFormData.email)) {
      // In a real implementation, you would show an error message
      // For now, we'll just call the original onSubmit
      alert('Please enter a valid email address');
      return;
    }
    
    // Call the original onSubmit handler
    onSubmit(e);
  };

  return (
    <div className="modal fade show d-block" tabIndex={-1} style={{ 
      backgroundColor: 'rgba(0,0,0,0.5)',
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      zIndex: 1050,
      overflow: 'auto'
    }}>
      <div className="modal-dialog modal-dialog-centered modal-lg" style={{ 
        margin: '1.75rem auto',
        position: 'relative',
        zIndex: 1051,
        maxWidth: '700px',
        width: '90%'
      }}>
        <div className="modal-content border-0 shadow-lg">
          <div className="modal-header py-3" style={{
            background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
            color: 'white'
          }}>
            <h5 className="modal-title fw-bold">
              <i className="bi bi-person-plus me-2"></i>
              {title}
            </h5>
            <button 
              type="button" 
              className="btn-close btn-close-white" 
              onClick={onClose}
            ></button>
          </div>
          <form onSubmit={handleSubmit}>
            <div className="modal-body py-3 text-start">
              {formError && (
                <div className="alert alert-danger alert-dismissible fade show" role="alert">
                  <i className="bi bi-exclamation-triangle-fill me-2"></i>
                  {formError}
                </div>
              )}
              {formSuccess && (
                <div className="alert alert-success alert-dismissible fade show" role="alert">
                  <i className="bi bi-check-circle-fill me-2"></i>
                  {formSuccess}
                </div>
              )}
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label fw-medium text-start">Name<span className="text-danger ms-1">*</span></label>
                  <input
                    type="text"
                    className="form-control form-control-lg text-start"
                    name="name"
                    value={initialFormData.name}
                    onChange={onFormChange}
                    required
                    placeholder="Enter full name"
                  />
                </div>
                <div className="col-md-6 mb-3">
                  <label className="form-label fw-medium text-start">Email<span className="text-danger ms-1">*</span></label>
                  <div className="input-group">
                    <input
                      type="email"
                      className="form-control form-control-lg text-start"
                      name="email"
                      value={initialFormData.email}
                      onChange={onFormChange}
                      required
                      placeholder="Enter email address"
                    />
                    <span className="input-group-text bg-white">
                      <i className="bi bi-envelope"></i>
                    </span>
                  </div>
                </div>
                <div className="col-md-6 mb-3">
                  <label className="form-label fw-medium text-start">Phone<span className="text-danger ms-1">*</span></label>
                  <input
                    type="tel"
                    className="form-control form-control-lg text-start"
                    name="phone"
                    value={initialFormData.phone}
                    onChange={onFormChange}
                    maxLength={10}
                    required
                    placeholder="Enter 10-digit phone number"
                  />
                </div>
                
                {/* Password field only shown when creating a new user */}
                {!isEditMode && (
                  <div className="col-md-6 mb-3">
                    <label className="form-label fw-medium text-start">Password<span className="text-danger ms-1">*</span></label>
                    <div className="input-group">
                      <input
                        type={showPassword ? "text" : "password"}
                        className="form-control form-control-lg text-start"
                        name="password"
                        value={initialFormData.password || ''}
                        onChange={onFormChange}
                        required
                        placeholder="Enter secure password"
                      />
                      <span className="input-group-text bg-white">
                        <i 
                          className={`bi ${showPassword ? 'bi-eye-slash' : 'bi-eye'} cursor-pointer`} 
                          onClick={() => setShowPassword(!showPassword)}
                        ></i>
                      </span>
                    </div>
                  </div>
                )}
                
                <div className="col-md-6 mb-3">
                  <label className="form-label fw-medium text-start">Company Name<span className="text-danger ms-1">*</span></label>
                  {loadingCompanies ? (
                    <div className="form-control form-control-lg text-start">
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Loading companies...
                    </div>
                  ) : (
                    <div className="d-flex gap-2 align-items-center">
                      <select
                        className="form-select form-select-lg text-start flex-grow-1"
                        name="companyId"
                        value={initialFormData.companyId}
                        onChange={onFormChange}
                        required
                      >
                        <option value="">Select a company</option>
                        {companies.map((company: any) => (
                          <option key={company._id} value={company._id}>
                            {company.companyName}
                          </option>
                        ))}
                      </select>
                    </div>                  
                  )}
                </div>
                <div className="col-md-6 mb-3">
                  <label className="form-label fw-medium text-start">User Type<span className="text-danger ms-1">*</span></label>
                  <select
                    className="form-select form-select-lg text-start"
                    name="usertype"
                    value={initialFormData.usertype}
                    onChange={onFormChange}
                    required
                  >
                    {userTypeOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-6 mb-3">
                  <label className="form-label fw-medium text-start">Country</label>
                  <input
                    type="text"
                    className="form-control form-control-lg text-start"
                    name="country"
                    value={initialFormData.country || ''}
                    onChange={onFormChange}
                    placeholder="Enter country"
                  />
                </div>
                <div className="col-md-6 mb-3">
                  <label className="form-label fw-medium text-start">State</label>
                  <input
                    type="text"
                    className="form-control form-control-lg text-start"
                    name="state"
                    value={initialFormData.state || ''}
                    onChange={onFormChange}
                    placeholder="Enter state"
                  />
                </div>
                <div className="col-md-6 mb-3">
                  <label className="form-label fw-medium text-start">City</label>
                  <input
                    type="text"
                    className="form-control form-control-lg text-start"
                    name="city"
                    value={initialFormData.city || ''}
                    onChange={onFormChange}
                    placeholder="Enter city"
                  />
                </div>
                <div className="col-md-6 mb-3">
                  <label className="form-label fw-medium text-start">Gender</label>
                  <select
                    className="form-select form-select-lg text-start"
                    name="gender"
                    value={initialFormData.gender || ''}
                    onChange={onFormChange}
                  >
                    <option value="">Select gender</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                {(userRole === 'ADMIN' || userRole === 'COMPANY_OWNER') && (
                  <div className="col-md-6 mb-3">
                    <label className="form-label fw-medium text-start">Department</label>
                    {loadingDepartments ? (
                      <div className="form-control form-control-lg text-start">
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        Loading departments...
                      </div>
                    ) : (
                      <select
                        className="form-select form-select-lg text-start"
                        name="departmentId"
                        value={initialFormData.departmentId || ''}
                        onChange={onFormChange}
                      >
                        <option value="">Select a department (optional)</option>
                        {departments.map((department: any) => (
                          <option key={department._id} value={department._id}>
                            {department.name}
                          </option>
                        ))}
                      </select>
                    )}
                    <div className="form-text">
                      Select department for this user (optional)
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="modal-footer py-3 bg-light">
              <button 
                type="button" 
                className="btn btn-outline-primary px-4 py-2" 
                onClick={onClose}
                disabled={formLoading}
                style={{
                  color: '#1e3c72',
                  borderColor: '#1e3c72',
                }}
                onMouseEnter={(e) => {
                  if (!formLoading) {
                    e.currentTarget.style.backgroundColor = '#1e3c72';
                    e.currentTarget.style.color = 'white';
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.color = '#1e3c72';
                }}
              >
                <i className="bi bi-x-circle me-1"></i>
                Cancel
              </button>
              <button 
                type="submit" 
                className="btn btn-primary px-4 py-2"
                disabled={formLoading}
                style={{
                  background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
                  border: 'none'
                }}
              >
                {formLoading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2"></span>
                    {isEditMode ? 'Updating...' : 'Creating...'}
                  </>
                ) : (
                  <>
                    <i className="bi bi-check-circle me-1"></i>
                    {isEditMode ? 'Update User' : 'Create User'}
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default UserFormModal;